import React from 'react';
import {
	Box,
	Typography,
	Stack,
	alpha,
	useTheme,
	useMediaQuery,
	Tooltip,
	IconButton,
	TextField,
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { formatCurrency } from '../../../../Shared/stripeFee';
import { BID_STATUS, resolveBalanceDue } from '../../../../Shared/bids';
import { surfaceCard } from '../../../../Shared/surfaceCard';
import JobDocuments from './JobDocuments';
import JobDueDate from './JobDueDate';
import JobHeroSite from './JobHeroSite';

const JobHero = ({
	customerName,
	jobName,
	description,
	isEditingDescription,
	onDescriptionToggle,
	onDescriptionChange,
	onDescriptionConfirm,
	dueDate,
	onDueDateChange,
	job,
	onSiteSave,
	bidAmount,
	bidStatus,
	bidDetails,
	depositPaidAmount = 0,
	refundedAmount = 0,
	refundSummary = null,
	showDepositSummary = false,
	onCustomerEdit,
	customerEditLoading,
}) => {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('md'));
	const depositPaid = Number(depositPaidAmount) || 0;
	const refunded = Number(refundedAmount) || 0;
	const depositRefunded = Number(
		refundSummary?.breakdown?.deposit?.refunded ?? 0,
	);
	const effectiveDepositPaid = Math.max(0, depositPaid - depositRefunded);
	const bidTotal = Number(bidAmount) || 0;
	const isFullyPaid = bidStatus === BID_STATUS.PAID;
	const balanceDue = isFullyPaid
		? 0
		: resolveBalanceDue(
				bidDetails || { amount: bidTotal },
				effectiveDepositPaid,
			);
	const showPaymentSummary =
		showDepositSummary &&
		bidTotal > 0 &&
		(isFullyPaid ||
			effectiveDepositPaid > 0 ||
			refunded > 0 ||
			bidStatus === BID_STATUS.PENDING_PAYMENT ||
			bidStatus === BID_STATUS.DEPOSIT_PAID ||
			bidStatus === BID_STATUS.PENDING_DEPOSIT);

	const customerChip = (
		<Tooltip title="Edit customer">
			<Box
				component="button"
				type="button"
				onClick={onCustomerEdit}
				disabled={customerEditLoading}
				sx={{
					display: 'inline-flex',
					alignItems: 'center',
					gap: 0.75,
					px: 1.25,
					py: 0.5,
					mb: 0.75,
					border: '1px solid',
					borderColor: (t) =>
						t.palette.mode === 'dark'
							? alpha(t.palette.common.white, 0.12)
							: alpha(t.palette.common.black, 0.1),
					borderRadius: 2,
					bgcolor: 'transparent',
					cursor: customerEditLoading ? 'wait' : 'pointer',
					color: 'text.secondary',
					fontFamily: 'inherit',
					fontSize: '0.68rem',
					fontWeight: 600,
					letterSpacing: 1.2,
					textTransform: 'uppercase',
					opacity: customerEditLoading ? 0.6 : 1,
					transition: theme.transitions.create([
						'border-color',
						'background-color',
						'color',
					]),
					'&:hover:not(:disabled)': {
						borderColor: (t) => alpha(t.palette.primary.main, 0.45),
						bgcolor: (t) => alpha(t.palette.primary.main, 0.06),
						color: 'primary.main',
						'& .customer-edit-icon': {
							opacity: 1,
							color: 'primary.main',
						},
					},
				}}
			>
				{customerName}
				<EditOutlinedIcon
					className="customer-edit-icon"
					sx={{ fontSize: 14, opacity: 0.55, transition: 'inherit' }}
				/>
			</Box>
		</Tooltip>
	);

	const descriptionBlock = (
		<Box sx={{ mt: { xs: 1.25, md: 1.5 } }}>
			<Box
				sx={{
					display: 'flex',
					alignItems: 'flex-start',
					justifyContent: 'space-between',
					gap: 1,
				}}
			>
				<Typography
					variant="caption"
					sx={{
						display: 'block',
						fontWeight: 600,
						color: 'text.secondary',
						textTransform: 'uppercase',
						letterSpacing: 0.6,
						mb: 0.5,
					}}
				>
					Description
				</Typography>
				<Tooltip title={isEditingDescription ? 'Save' : 'Edit description'}>
					<IconButton
						size="small"
						onMouseDown={onDescriptionToggle}
						sx={{
							mt: -0.5,
							color: isEditingDescription ? 'primary.main' : 'text.secondary',
						}}
					>
						<EditIcon sx={{ fontSize: 16 }} />
					</IconButton>
				</Tooltip>
			</Box>
			{isEditingDescription ? (
				<TextField
					fullWidth
					multiline
					value={description || ''}
					minRows={2}
					maxRows={6}
					autoFocus
					placeholder="No description provided."
					onChange={(e) => onDescriptionChange(e.target.value)}
					onBlur={(e) => onDescriptionConfirm(e.target.value)}
					size="small"
					sx={{
						'& .MuiOutlinedInput-root': {
							borderRadius: 2,
							bgcolor: (t) =>
								t.palette.mode === 'dark'
									? alpha(t.palette.common.white, 0.04)
									: alpha(t.palette.common.black, 0.02),
						},
					}}
				/>
			) : (
				<Typography
					variant="body2"
					sx={{
						whiteSpace: 'pre-wrap',
						lineHeight: 1.65,
						color: description ? 'text.primary' : 'text.secondary',
						fontStyle: description ? 'normal' : 'italic',
					}}
				>
					{description || 'No description provided.'}
				</Typography>
			)}
		</Box>
	);

	const bidColumn = (
		<Box
			sx={{
				flexShrink: 0,
				textAlign: { xs: 'left', md: 'right' },
				width: { xs: '100%', md: 'auto' },
				minWidth: { md: 160 },
			}}
		>
			<Typography
				variant="caption"
				sx={{
					display: 'block',
					fontWeight: 600,
					color: 'text.secondary',
					textTransform: 'uppercase',
					letterSpacing: 0.8,
					fontSize: '0.65rem',
					mb: 0.5,
				}}
			>
				Current Bid
			</Typography>
			<Typography
				variant={isMobile ? 'h4' : 'h3'}
				sx={{
					fontWeight: 800,
					letterSpacing: -1,
					fontVariantNumeric: 'tabular-nums',
					color: 'primary.main',
					lineHeight: 1.1,
				}}
			>
				${formatCurrency(bidAmount)}
			</Typography>
			{showPaymentSummary ? (
				<Stack spacing={0.25} sx={{ mt: 1.25 }}>
					{isFullyPaid ? (
						<Stack
							direction="row"
							alignItems="center"
							spacing={0.75}
							justifyContent={{ xs: 'flex-start', md: 'flex-end' }}
						>
							<CheckCircleOutlineIcon
								sx={{ fontSize: 20, color: 'success.main' }}
								aria-hidden
							/>
							<Typography
								variant="body2"
								sx={{ fontWeight: 700, color: 'success.main' }}
							>
								Paid in full
							</Typography>
						</Stack>
					) : null}
					{depositPaid > 0 ? (
						<Typography
							variant="body2"
							sx={{
								fontVariantNumeric: 'tabular-nums',
								color: 'text.secondary',
							}}
						>
							Deposit received:{' '}
							<Box
								component="span"
								sx={{ fontWeight: 700, color: 'success.main' }}
							>
								${formatCurrency(depositPaid)}
							</Box>
						</Typography>
					) : null}
					{refunded > 0 ? (
						<Typography
							variant="body2"
							sx={{
								fontVariantNumeric: 'tabular-nums',
								color: 'text.secondary',
							}}
						>
							Refunded:{' '}
							<Box
								component="span"
								sx={{ fontWeight: 700, color: 'warning.main' }}
							>
								${formatCurrency(refunded)}
							</Box>
						</Typography>
					) : null}
					{!isFullyPaid && balanceDue > 0 ? (
						<Typography
							variant="body2"
							sx={{
								fontVariantNumeric: 'tabular-nums',
								color: 'text.secondary',
							}}
						>
							Balance due:{' '}
							<Box component="span" sx={{ fontWeight: 700 }}>
								${formatCurrency(balanceDue)}
							</Box>
						</Typography>
					) : null}
					{!isFullyPaid &&
					effectiveDepositPaid > 0 &&
					balanceDue <= 0 &&
					bidStatus === BID_STATUS.DEPOSIT_PAID ? (
						<Typography
							variant="body2"
							sx={{ fontWeight: 600, color: 'success.main' }}
						>
							Deposit complete - invoice pending
						</Typography>
					) : null}
				</Stack>
			) : null}
			<Box sx={{ mt: 1.5, display: { xs: 'none', lg: 'block' } }}>
				<JobDocuments
					proposalPdfUrl={bidDetails?.proposal_pdf_url}
					invoicePdfUrl={bidDetails?.invoice_pdf_url}
				/>
			</Box>
		</Box>
	);

	const metaColumn = (
		<Stack
			spacing={1.25}
			sx={{
				width: '100%',
				minWidth: 0,
			}}
		>
			<JobDueDate
				variant="hero"
				dueDate={dueDate}
				onDueDateChange={onDueDateChange}
			/>
			<JobHeroSite job={job} onSave={onSiteSave} />
		</Stack>
	);

	return (
		<Box
			sx={{
				...surfaceCard(theme),
				mb: { xs: 2.5, md: 3 },
				p: { xs: 2.5, sm: 3, md: 3 },
				background: (t) =>
					t.palette.mode === 'dark'
						? `linear-gradient(135deg, ${alpha(t.palette.primary.main, 0.14)} 0%, ${alpha(t.palette.common.white, 0.03)} 55%, ${alpha(t.palette.common.white, 0.02)} 100%)`
						: `linear-gradient(135deg, ${alpha(t.palette.primary.main, 0.06)} 0%, ${t.palette.background.paper} 60%)`,
			}}
		>
			{/* Large screens: identity | meta (fills middle) | bid */}
			<Box
				sx={{
					display: { xs: 'none', md: 'grid' },
					gridTemplateColumns: 'minmax(0, 1.4fr) minmax(200px, 0.9fr) auto',
					gap: 3,
					alignItems: 'start',
				}}
			>
				<Box sx={{ minWidth: 0 }}>
					{customerChip}
					<Typography
						variant="h4"
						fontWeight={800}
						sx={{
							mt: 0.25,
							letterSpacing: -0.5,
							lineHeight: 1.2,
							wordBreak: 'break-word',
						}}
					>
						{jobName}
					</Typography>
					{descriptionBlock}
				</Box>
				<Box
					sx={{
						pt: 0.5,
						borderLeft: '1px solid',
						borderColor: 'divider',
						pl: 3,
						minWidth: 0,
					}}
				>
					{metaColumn}
				</Box>
				{bidColumn}
			</Box>

			{/* Small screens: stacked */}
			<Stack
				spacing={2}
				sx={{ display: { xs: 'flex', md: 'none' } }}
			>
				<Box sx={{ minWidth: 0 }}>
					{customerChip}
					<Typography
						variant="h5"
						fontWeight={800}
						sx={{
							mt: 0.25,
							letterSpacing: -0.5,
							lineHeight: 1.2,
							wordBreak: 'break-word',
						}}
					>
						{jobName}
					</Typography>
					{descriptionBlock}
				</Box>
				{metaColumn}
				<Box
					sx={{
						pt: 1.5,
						borderTop: '1px solid',
						borderColor: 'divider',
					}}
				>
					{bidColumn}
				</Box>
			</Stack>
		</Box>
	);
};

export default JobHero;
